import classNames from "classnames";
import {
	FormattedText,
	ListItem,
	NoResults,
	VirtualList
} from "client/Components";
import { contains, normalizeForSearch } from "client/Data/searching";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNilOrEmpty
} from "client/Misc/utils";
import { partition, sortBy } from "client_legacy/ndaw/arrayHelpers";
import { localize } from "client_legacy/ndaw/culture";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef
} from "react";
import {
	GroupHierarchyGraph,
	GroupHierarchyGraphHandle,
	UserGroupWithRelationsModel
} from "./GroupHierarchyGraph";
import styles from "./VisualView.module.less";

export type VisualViewProps = {
	groups: readonly UserGroupWithRelationsModel[];
	searchTerm: string;
	selectedGroupId: number | null;
	onChangeSelectedGroupId: (id: number) => void;
};

export const VisualView = (props: VisualViewProps): ReactElement => {
	const {
		groups,
		searchTerm,
		selectedGroupId,
		onChangeSelectedGroupId
	} = props;

	const scrollableListRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const graphRef: RefObject<GroupHierarchyGraphHandle | null> = useRef<GroupHierarchyGraphHandle>(null);

	const groupListInformation = useMemo(
		(): readonly GroupListInformation[] => {
			const groupMap: Map<number, GroupListInformation> = fromArray(
				groups,
				(x) => x.id,
				(x): GroupListInformation => ({
					id: x.id,
					name: x.name,
					members: x.members
						.length,
					// The below fields will be filled in the next step
					memberOf: 0,
					isCyclic: false
				})
			);

			const groupWithDirectRelationsMap: Map<number, readonly number[]> = fromArray(
				groups,
				(x) => x.id,
				(x) => x.members
			);

			for (const group of groups) {
				for (const childGroupId of group.members) {
					const childGroup: GroupListInformation | undefined = groupMap.get(childGroupId);
					if (!childGroup) {
						console.warn(`Incomplete group data, '${childGroupId}' is mentioned as member, but is not present in the groups data`);
					} else {
						// No need to write back to the map, the reference remains te same
						childGroup.memberOf++;
					}
				}

				const visitedGroupIds = new Set<number>();
				const isCycle: boolean = isPartOfCycle(
					group.id,
					group.id,
					visitedGroupIds,
					groupWithDirectRelationsMap
				);

				ensureNotNil(
					groupMap.get(group.id)
				)
					.isCyclic = isCycle;
			}

			return Array.from(
				groupMap.values()
			);
		},
		[groups]
	);

	const [withRelations, withoutRelations] = useMemo(
		(): [readonly GroupListInformation[], readonly GroupListInformation[]] => {
			return partition(
				sortBy(
					groupListInformation,
					(x) => x.name
				),
				(x) => x.members + x.memberOf > 0
			);
		},
		[groupListInformation]
	);

	const [withRelationsToRender, withoutRelationsToRender] = useMemo(
		(): [readonly GroupListInformation[], readonly GroupListInformation[]] => {
			if (!searchTerm) {
				return [withRelations, withoutRelations];
			}

			const normalizedSearchTerm: string = normalizeForSearch(searchTerm);

			return [
				withRelations.filter((x) => contains(x.name, normalizedSearchTerm)),
				withoutRelations.filter((x) => contains(x.name, normalizedSearchTerm))
			];
		},
		[
			searchTerm,
			withRelations,
			withoutRelations
		]
	);

	useEffect(
		(): void => {
			scrollableListRef.current
				?.scrollTo(0, 0);
		},
		[searchTerm]
	);

	const selectedGroupWithFocusHandler = useCallback(
		(id: number): void => {
			onChangeSelectedGroupId(id);

			graphRef.current
				?.focusOn(id);
		},
		[onChangeSelectedGroupId]
	);

	const hasSearch: boolean = isNotNilOrEmpty(searchTerm);

	return (
		<div className={styles.root}>
			<div
				className={styles.listHost}
				ref={scrollableListRef}
			>
				{withRelationsToRender.length === 0 && withoutRelationsToRender.length === 0 && (
					<NoResults
						icon={hasSearch ? "magnifying-glass" : "user-group"}
						title={localize(hasSearch ? "_NoSearchResults" : "_NoGroupsYet")}
						message={localize(hasSearch ? "_PleaseTrySearchingWithAnotherTerm" : "_NoGroupsHaveBeenAdded")}
						height="fill"
						trackingId="no-results"
					/>
				)}

				{withRelationsToRender.length > 0 && (
					<WithRelationsSection
						groups={withRelationsToRender}
						selectedGroupId={selectedGroupId}
						onChangeSelectedGroupId={selectedGroupWithFocusHandler}
					/>
				)}

				{withoutRelationsToRender.length > 0 && (
					<WithoutRelationsSection groups={withoutRelationsToRender} />
				)}
			</div>

			<div className={styles.graphHost}>
				<GroupHierarchyGraph
					groups={groups}
					selectedGroupId={selectedGroupId}
					onChangeSelectedGroupId={onChangeSelectedGroupId}
					ref={graphRef}
					trackingId="graph"
				/>
			</div>
		</div>
	);
};
VisualView.displayName = "VisualView";

type WithRelationsSectionProps = {
	groups: readonly GroupListInformation[];
	selectedGroupId: number | null;
	onChangeSelectedGroupId: (id: number) => void;
};

const WithRelationsSection = (props: WithRelationsSectionProps): ReactElement => {
	const {
		groups,
		selectedGroupId,
		onChangeSelectedGroupId
	} = props;

	return (
		<>
			<h4 className={styles.listHeader}>
				{localize("_GroupsWithRelations")}
			</h4>

			<VirtualList
				items={groups}
				itemKey="id"
				estimatedHeightOfAnItem={81}
				trackingId="groups-with-relations"
			>
				{(x): ReactElement => (
					<ListItem
						isActive={x.id === selectedGroupId}
						trackingId={generateTrackingId("group", `|${x.id}`)}
					>
						<ListItem.Content
							onClick={() => onChangeSelectedGroupId(x.id)}
						>
							<ListItem.Title tooltip={x.name}>
								<span className={styles.titleWithPossibleIcon}>
									<span className={styles.titleOfTitleWithPossibleIcon}>
										{x.name}
									</span>
									{x.isCyclic && (
										<ListItem.TitleIcon
											kind="triangle-exclamation"
											color="warning"
											tooltip={localize("_ThereAreLoopsInTheHierarchyOfTheGroup")}
											trackingId="cyclic"
										/>
									)}
								</span>
							</ListItem.Title>
							<ListItem.SubTitle>
								<span
									className={classNames(
										styles.relationIcon,
										styles.relationIcon_member
									)}
								/>

								<FormattedText
									text={localize("_MembersAmount")}
									thingsToReplace={[
										{ what: "{{Amount}}", filler: x.members }
									]}
								/>
							</ListItem.SubTitle>
							<ListItem.SubTitle>
								<span
									className={classNames(
										styles.relationIcon,
										styles.relationIcon_memberOf
									)}
								/>

								<FormattedText
									text={localize("_MembersOfAmount")}
									thingsToReplace={[
										{ what: "{{Amount}}", filler: x.memberOf }
									]}
								/>
							</ListItem.SubTitle>
						</ListItem.Content>
					</ListItem>
				)}
			</VirtualList>
		</>
	);
};
WithRelationsSection.displayName = "WithRelationsSection";

type WithoutRelationsSectionProps = {
	groups: readonly GroupListInformation[];
};

const WithoutRelationsSection = (props: WithoutRelationsSectionProps): ReactElement => {
	const {
		groups
	} = props;

	return (
		<>
			<h4 className={styles.listHeader}>
				{localize("_GroupsWithoutRelations")}
			</h4>

			<VirtualList
				items={groups}
				itemKey="id"
				estimatedHeightOfAnItem={63}
				trackingId="groups-without-relations"
			>
				{(x): ReactElement => (
					<ListItem
						interactionMode="none"
						trackingId={generateTrackingId("group", `|${x.id}`)}
					>
						<ListItem.Content>
							<ListItem.Title
								variant="secondary"
								tooltip={x.name}
							>
								{x.name}
							</ListItem.Title>
						</ListItem.Content>
					</ListItem>
				)}
			</VirtualList>
		</>
	);
};
WithoutRelationsSection.displayName = "WithoutRelationsSection";

type GroupListInformation = {
	id: number;
	name: string;
	members: number;
	memberOf: number;
	isCyclic: boolean;
};

function isPartOfCycle(
	groupId: number,
	currentGroupId: number,
	visitedGroupIds: Set<number>,
	groupsDirectRelations: Map<number, readonly number[]>
): boolean {
	visitedGroupIds.add(currentGroupId);

	for (const subGroupId of groupsDirectRelations.get(currentGroupId) ?? []) {
		if (subGroupId === groupId) {
			return true;
		}

		if (visitedGroupIds.has(subGroupId)) {
			continue;
		}

		const result: boolean = isPartOfCycle(
			groupId,
			subGroupId,
			visitedGroupIds,
			groupsDirectRelations
		);

		if (result) {
			return result;
		}
	}

	return false;
}
